import {Component, Input, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges, HostListener} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { OptionService } from "../option/option.service";
import {Donation} from "../donation/donation";
import {StatusComponent} from "../output/output.status.component";
import {GlobalValidators} from "../validate/validate.globalvalidators";
import {Contact} from "../contact/contact";
import {HttpClient} from "@angular/common/http";
import {getIconForPaymentMethod} from "./payment.helper";
import {StripeService} from "./payment.stripe.service";
import { GenderService } from "../service/gender.service";
import { EmailService } from 'service/email.service';
import {TranslateService} from "@ngx-translate/core";

declare let window: any;

interface BankResponse {
    success: boolean;
    data: any;
}

@Component({
    selector: 'payment',
    templateUrl: './payment.component.html'
})
export class PaymentComponent implements OnInit, OnDestroy, OnChanges {
    /**
     * The current donation
     */
    @Input() donation: Donation;

    /**
     * the donation rythm
     *
     */
    @Input() rythm: string = null;

    /**
     * the donation amount
     */
    @Input() amount: number = null;

    /**
     * The contact data of the user
     */
    @Input() contact: Contact;

    /**
     * form for validating the inputs
     */
    @Input() form: FormGroup;

    /**
     * Status of the current donation
     */
    @Input() donationStatus: string;

    /**
     * The current step of the app
     */
    @Input() step: string;

    /**
     * The current size of the users viewport
     */
    @Input() size: string;

    /**
     * The version of the app (widget, form, page)
     */
    @Input() version: string;

    /**
     * Output parameter for changing the step
     * @type {EventEmitter}
     */
    @Output() stepChange: EventEmitter<string> = new EventEmitter();

    /**
     * Parameter for sending the output
     * @type {EventEmitter}
     */
    @Output() donationStatusChange: EventEmitter<any> = new EventEmitter();

    /**
     * holds the available payment methods
     */
    paymentMethods: Array<any>;

    /**
     * Show or hide the infotext fpr SEPA checkbox
     * @type {boolean}
     */
    showInfotext: boolean = false;

    /**
     * Textkey for the BIC Label
     * @type {string}
     */
    bicLabel: string = '';

    /**
     * The type of the application
     * @type {{}}
     */
    type: string = null;

    /**
     * List of the available payment methods for the current donation
     */
    availablePaymentMethods: Array<string> = [];

    /**
     * toggle if the bic is needed for this iban or not.
     */
    needBic:boolean = false;

    /**
     * do we need the sepa form
     */
    isSepa: boolean = false;

    /**
     * toggle if the current payment method needs a redirect to six/saferpay
     */
    isSix: boolean = false;

    /**
     * toggle if the first- and lastname is mandatory
     */
    nameMandatory: boolean = false;

    /**
     * whether to show the salutation or not
     */
    showSalutation: boolean = false;

    /**
     * List of fields to be excluded from the form
     * @type {Array}
     */
    excludedFields: string[] = [];

    /**
     * shows the sepa recommendation label
     */

    hasRecommendation: boolean = false;

    /**
     * holds the sepa recommendation for each payment method
     */
    recommendationForPayment: { [paymentMethod: string]: boolean } = {};

    /**
     * label text for the sepa recommendation
     */
    recommendationLabel: string = 'payment.recommendation.hint';

    /**
     * do we have any sepa payment method available
     */
    hasSepa: boolean = false;

    /**
     * show the hint for changing to sepa payment
     */
    hasSepaChangeHint: boolean = false;

    /**
     * check if contact is mandatory
     */
    isContactMandatory: boolean = false;

    sepaText: string;

    /**
     * List of payment methods that need a redirect
     * @type {[string,string,string,string]}
     */
    static redirectPaymentMethods: string[] = ['sixpay', 'paypal', 'paypal_guest', 'sofortueberweisung', 'mbe4', 'apple_pay', 'paydirekt', 'twint', 'ideal', 'post_finance', 'google_pay', 'bancontact', 'stripe_apple_pay'];

    /**
     * list of payment methods for six/saferpay
     */
    static sixPaymentMethods: string[] = ['sixpay', 'apple_pay', 'twint', 'ideal', 'post_finance', 'google_pay', 'bancontact'];

    /**
     * List of countries where we have BIC data
     */
    static bicDataCountries: any = {
        'DE': {length: 22, bankNumberStart: 4, bankNumberLength: 8},
        'AT': {length: 20, bankNumberStart: 4, bankNumberLength: 5},
        'ES': {length: 24, bankNumberStart: 4, bankNumberLength: 4}
    };

    /**
     * List of IBAN Countrycodes where an address is mandatory
     */
    static ibanCodesWhereAddressIsNeeded = ["AL", "AD", "BA", "BV", "FO", "TF", "PF", "GL", "GG", "JE", "XK", "IM", "MU", "MK", "MC", "ME", "NC", "SM", "CH", "RS", "SJ", "PM", "TR", "VA", "WF", "GB"];

    /**
     * Is called if the user clicks the body
     * @param event
     */
    @HostListener('document:click', ['$event'])
    onClickOutside(event: any): void {
        if (this.showInfotext === true && event.target.localName !== "a") {
            this.showInfotext = false;
        }
    }

    /**
     * Hook if the rhytm or the amount is changed
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if ((changes.amount && changes.amount.firstChange === false) || (changes.rythm && changes.rythm.firstChange === false)) {
            this.updateAvailablePaymentMethdods();

            // update stripe element for amount chnages
            if (this.donation.paymentMethod?.startsWith('stripe_') && changes.amount && changes.amount.firstChange === false) {
              this.stripeService.updateAmount(this.donation.amount);
            }

            // remount stripe element for rythm changes
            if (this.donation.paymentMethod?.startsWith('stripe_') && changes.rythm && changes.rythm.firstChange === false) {
              this.stripeService.updateRecurring(this.donation.recurringRythm !== null);
            }
        }
    }

    /**
     * The constructor
     * @param optionService
     * @param http
     * @param changeDetector
     * @param stripeService
     * @param genderService
     * @param emailService
     * @param translate
     */
    constructor(private optionService: OptionService, private http: HttpClient, private changeDetector: ChangeDetectorRef, private stripeService: StripeService,
                private genderService: GenderService, private emailService: EmailService, private translate: TranslateService) { }

    /**
     * Validate if a payment method can be used in the current browser.
     *
     * @param paymentMethod Can be one of apple_pay or google_pay
     */
    public static isPaymentMethodAvailable(paymentMethod: string): boolean {
        if (paymentMethod.includes('apple_pay')) {
          if (location.host.startsWith('public.twingle.loc')) {
            // show apple pay on local dev system.
            return true;
          }

          try {
            return typeof window['ApplePaySession'] !== "undefined" && window['ApplePaySession'].canMakePayments() === true;
          } catch (e) {
            // ignore errors (i.e. non ssl), apple pay is not available.
            // there could be also an error if the security domain of the app and it's top frame is different (90% of twingle cases)
            // fallback: Only check if JS-libray is available, this could give some fals positive
            return typeof window['ApplePaySession'] !== "undefined";
          }
        }

        return true;
    }

    /**
     * Returns a list of the available paymentMethdods, based on the rhythm selection
     * @returns {Array<string>}
     */
    updateAvailablePaymentMethdods() {
        this.availablePaymentMethods = this.paymentMethods;

        // amount less than possible with MBE
        if(this.donation.amount > this.optionService.getMbe4MaxAmount()) {
            this.availablePaymentMethods = this.availablePaymentMethods.filter(function(value) {return value != "mbe4"});
        }

        // filter the payment methods based on the selected payment rhythm
        if((this.amount + this.donation.extraAmount) === 0) {
            // special case: 0 eur transaction, can happen in the shop
            this.availablePaymentMethods = ['banktransfer'];
        }
        else if(this.donation.rythm != 'one_time') {
            // filter the payment methods if recurring
            this.availablePaymentMethods = this.availablePaymentMethods.filter(function(value) {
                return value.indexOf("_recurring") !== -1;
            });

            // special case paypal, only recurring if activated
            if(this.availablePaymentMethods.indexOf("paypal") !== -1 && this.optionService.getOrganisationData("paypal_recurring") !== true) {
                // remove paypal here, it's not activated
                this.availablePaymentMethods.splice(this.availablePaymentMethods.indexOf("paypal"), 1);
            }

            // rename recurring payment methods
            let that = this;
            this.availablePaymentMethods.forEach(function(value, index) {
                that.availablePaymentMethods[index] = value.replace(/_recurring/, "");
            });
        }
        else if (this.donation.rythm === 'one_time') {
            // filter the payment methods if NOT recurring
            this.availablePaymentMethods = this.availablePaymentMethods.filter(function(value) {
                return value.indexOf("_recurring") === -1;
            });
        }

        // check if the current selection is still valid
        if(this.donation.paymentMethod != null && this.availablePaymentMethods.indexOf(this.donation.paymentMethod) == -1) {

            if(this.availablePaymentMethods.length == 1) {
                // preselect the first available paymentmethod
                this.donation.paymentMethod = this.availablePaymentMethods[0];
            }
            else {
                this.donation.paymentMethod = null;
            }
            // update form with validation
            this.changePaymentMethod(false);
        }

        // check google_pay
        if (this.availablePaymentMethods.filter(method => method.match(/google_pay/)).length > 0) {
          if (PaymentComponent.isPaymentMethodAvailable("google_pay") === false) {
            this.availablePaymentMethods = this.availablePaymentMethods.filter(method => !method.match(/google_pay/));
          }
        }

        // check apple pay
        if (this.availablePaymentMethods.filter(method => method.match(/apple_pay/)).length > 0) {
          if (PaymentComponent.isPaymentMethodAvailable("apple_pay") === false)  {
            this.availablePaymentMethods = this.availablePaymentMethods.filter(method => !method.match(/apple_pay/));
          }
        }

        // check if we have a sepa recommendation 
        if (this.availablePaymentMethods.length > 1) {
            this.availablePaymentMethods.forEach((paymentMethod) => {
                // set recommendation for debit, betterpayment and konfipay
                if (paymentMethod === 'debit' || paymentMethod === 'betterpayment' || paymentMethod === 'konfipay') {
                    this.hasSepa = true;
                    if (this.hasRecommendation || this.hasSepaChangeHint) {
                        this.recommendationForPayment[paymentMethod] = true;
                    }
                }
            })
        }
    }

    /**
     * check if we should show the recommendation
     */
    showRecommendation(): boolean {
        // recommendations active, sepa and one other payment available
        if (this.hasRecommendation && Object.keys(this.recommendationForPayment).length > 0  && this.availablePaymentMethods.length > 1) {
            return true;
        }

        // has sepa hint and current selection is banktransfer 
        if (this.hasSepaChangeHint && this.donation.paymentMethod === 'banktransfer') {
            return true;
        }

        return false;
    }

    /**
     * check if we should show the sepa
     */
    showSepaHint(): boolean {
        // do not show mobile on payment-selection step
        if (this.size === 'small' && this.step === 'payment-select') {
            return false;
        }
        // show if we selected banktransfer and the hint is active and we have at least one sepa payment method
        return this.donation.paymentMethod === 'banktransfer' && this.hasSepa && this.hasSepaChangeHint
    }

    /**
     * Function is called if the payment method is changed.
     */
    changePaymentMethod(preset: boolean): void {
        if(this.donationStatus != StatusComponent.OK) {
            this.donationStatus = StatusComponent.OK;
            this.donationStatusChange.emit(this.donationStatus);
        }

        // add sepa validators
        switch (this.donation.paymentMethod) {
            case 'debit':
            case 'betterpayment':
            case 'konfipay':
                this.isSepa = true;
                this.isSix = false;
                (<FormControl>this.form.controls['name']).enable({ onlySelf: true });
                (<FormControl>this.form.controls['iban']).enable({ onlySelf: true });
                (<FormControl>this.form.controls['bic']).enable({ onlySelf: true });
                (<FormControl>this.form.controls['terms']).enable({ onlySelf: true });
                break;
            default:
                this.isSepa = false;
                this.isSix = PaymentComponent.sixPaymentMethods.indexOf(this.donation.paymentMethod) !== -1;
                (<FormControl>this.form.controls['name']).disable({ onlySelf: true });
                (<FormControl>this.form.controls['iban']).disable({ onlySelf: true });
                (<FormControl>this.form.controls['bic']).disable({ onlySelf: true });
                (<FormControl>this.form.controls['terms']).disable({ onlySelf: true });
                break;
        }

        if (this.donation.paymentMethod === 'banktransfer' && this.hasSepaChangeHint) {
            // show a different label if the method is banktransfer and the sepa hint is active
            this.recommendationLabel = 'payment.recommendation.hint.banktransfer';
        } else {
            // show the default label everywhere else
            this.recommendationLabel = 'payment.recommendation.hint';
        }

        // email field
        if (['stripe_google_pay'].indexOf(this.donation.paymentMethod) !== -1) {
          // disable email and name fields because we get this data directly from stripe
          (<FormControl>this.form.controls['email']).disable({ onlySelf: true });
          if (this.nameMandatory === true) {
            (<FormControl>this.form.controls['firstname']).disable({onlySelf: true});
            (<FormControl>this.form.controls['lastname']).disable({onlySelf: true});
          }
        }
        else if (this.donation.paymentMethod && this.donation.paymentMethod !== 'mbe4' && this.donation.paymentMethod.length > 0) {
            (<FormControl>this.form.controls['email']).enable({ onlySelf: true });
            if (this.nameMandatory === true) {
                (<FormControl>this.form.controls['firstname']).enable({ onlySelf: true });
                (<FormControl>this.form.controls['lastname']).enable({ onlySelf: true });
            }
        }
        else {
            (<FormControl>this.form.controls['email']).disable({ onlySelf: true });
            if (this.nameMandatory === true) {
                (<FormControl>this.form.controls['firstname']).disable({ onlySelf: true });
                (<FormControl>this.form.controls['lastname']).disable({ onlySelf: true });
            }
        }

        // set the value of the donation method manually, otherwise this value is not there on small screens because the radios are not shown
        this.form.controls['payment_method'].setValue(this.donation.paymentMethod);
        this.form.controls['payment_method'].updateValueAndValidity();

        // if we are on the payment-selection step we change to the payment step
        if(this.step == "payment-select" && preset !== true && this.donation.paymentMethod) {
            this.step = "payment";
            this.stepChange.emit(this.step);
        }

        // show stripe form
        if (this.donation.paymentMethod === 'stripe_creditcard') {
          this.stripeService.unmountExpressCheckout();
          setTimeout(() => this.mountStripePayment(), 0);
        }
        else if (this.donation.paymentMethod === 'stripe_apple_pay') {
          // todo do something.
          this.stripeService.unmountExpressCheckout();
          this.unmountStripe();
        }
        else if (this.donation.paymentMethod === 'stripe_google_pay') {
          setTimeout(() => this.mountStripeExpresscheckout(this.donation.paymentMethod), 0);
        }
        else {
          this.stripeService.unmountExpressCheckout();
          this.unmountStripe();
        }

        // manuel change detection, otherwise the button does not know that the form validation status changed
        this.changeDetector.detectChanges();

        // check if we have contact data already => preset email field
        setTimeout(() => this.presetEmailAndName(), 0);
    }

    /**
     * Initialize the stripe payment.
     */
    async mountStripePayment() {
      // enable validation field and mount form
      this.form.controls['stripe_creditcard'].enable({onlySelf: false});
      this.stripeService.initPayment(this.donation.amount, this.donation.currency, this.donation.recurringRythm !== null, "payment");
    }

    /**
     * Initialize the stripe expresscheckout.
     */
    async mountStripeExpresscheckout(donationMethod: string) {
      // enable validation field and mount form
      this.form.controls['stripe_creditcard'].disable({onlySelf: false});
      this.stripeService.initExpressCheckout(this.donation.amount, this.donation.currency,
        "expresscheckout", donationMethod.replace('stripe_', ''), this.donation.recurringRythm !== null, this.form);
    }

    /**
     * unmount the stripe form.
     */
    unmountStripe() {
      // disable validation field
      this.form.controls['stripe_creditcard'].disable({onlySelf: false});
    }

    /**
     * Function that is called every time the IBAN changed
     */
    ibanChanged(): void {
        this.updateAddressFields();

        // get iban as trimmed
        let iban = this.donation.paymentDebit.iban.replace(/ /g, "");
        // get the country from the iban
        let country = iban.substr(0, 2).toUpperCase();

        if(typeof PaymentComponent.bicDataCountries[country] != "undefined") {
            // disbale field, we should have the data
            this.needBic = false;
            (<FormControl>this.form.controls['bic']).disable({ onlySelf: true });

            let config = PaymentComponent.bicDataCountries[country];

            if(iban.length == config.length) {
                // search for BIC
                let bankNumber = iban.substr(config.bankNumberStart, config.bankNumberLength);
                let that = this;

                this.http.get<BankResponse>(
                    OptionService.getLookupHost() + 'bank/number/' + country + '/' + bankNumber)
                    .subscribe(result => {
                            if(result && result.success && result.data.list && result.data.list.length > 0) {
                                that.donation.paymentDebit.bic = result.data.list[0].bic;
                                that.bicLabel = ' (' + result.data.list[0].description + ')';
                                // in some rare cases, the bic field was hidden in the meantime
                                if(that.form.controls.hasOwnProperty('bic')) {
                                    that.form.controls['bic'].setValue(result.data.list[0].bic);
                                    that.form.controls['bic'].updateValueAndValidity();
                                }
                            }
                            else {
                                console.warn("Got no bankdata or invalid data", result);
                                that.bicLabel = '';
                                this.needBic = true;

                                // enable field again
                                that.donation.paymentDebit.bic = null;
                                if(that.form.controls.hasOwnProperty('bic')) {
                                    that.form.controls['bic'].setValue(null);
                                    that.form.controls['bic'].updateValueAndValidity();
                                    that.form.controls['bic'].enable({onlySelf: true});
                                }
                            }
                        }
                    );
            }
        }
        else {
            // no BICs for this country, enable field
            (<FormControl>this.form.controls['bic']).enable({ onlySelf: true });
            this.bicLabel = '';
            this.needBic = country.length > 1 && (<FormControl>this.form.controls['iban']).valid;
        }
    }

    /**
     * Updates the validators of the address fields in case of
     */
    updateAddressFields(): void {
        if (this.isAddressNeeded()) {
            (<FormControl>this.form.controls['street_payment']).setValidators([Validators.required]);
            (<FormControl>this.form.controls['city_payment']).setValidators([Validators.required]);
            (<FormControl>this.form.controls['postal_code_payment']).setValidators([Validators.required]);

            (<FormControl>this.form.controls['street_payment']).enable({onlySelf: true});
            (<FormControl>this.form.controls['city_payment']).enable({onlySelf: true});
            (<FormControl>this.form.controls['postal_code_payment']).enable({onlySelf: true});
        }
        else {
            (<FormControl>this.form.controls['street_payment']).disable({onlySelf: true});
            (<FormControl>this.form.controls['city_payment']).disable({onlySelf: true});
            (<FormControl>this.form.controls['postal_code_payment']).disable({onlySelf: true});
        }
    }

    /**
     * Initialize the component
     */
    public ngOnInit(): any {
        this.type = this.optionService.getType();

        this.bicLabel = '';

        this.paymentMethods = this.optionService.getPaymentMethods();

        this.isContactMandatory = OptionService.getOptionStatic("has_contact_mandatory");

        // sepa recommendation
        this.hasRecommendation = this.optionService.getOrganisationData("has_sepa_recommendation");

        // change sepa hint
        this.hasSepaChangeHint = this.optionService.getOrganisationData("has_sepa_change_hint");

        // init stripe service if we have a stripe payment method and catch validation.
        if (this.paymentMethods.filter(method => method.indexOf('stripe') === 0).length > 0) {
          this.stripeService.initStripe();

          // listen for complete events of stripe service to update the validation
          this.stripeService.onChangeIsComplete.subscribe(complete => {
            this.form.controls['stripe_creditcard'].setValue(complete === true ? true : null);
          });

          this.stripeService.onStripeError.subscribe(() => {
            // there was an error on using stripe, deselect the current payment method.
            this.donation.paymentMethod = null;
            this.changePaymentMethod(false);
            if (this.size === 'small' && this.type !== 'form') {
              this.step = "payment-select";
              this.stepChange.emit(this.step);
            }
          });
        }

        this.form.addControl('payment_method', new FormControl());
        this.form.addControl('email', new FormControl('', [Validators.required, GlobalValidators.validateEmail]));
        this.form.addControl('name', new FormControl('', [Validators.required, Validators.maxLength(70)]));
        this.form.addControl('iban', new FormControl('', [Validators.required, GlobalValidators.validateIBAN]));
        this.form.addControl('bic', new FormControl('', [Validators.required, GlobalValidators.validateBIC]));
        this.form.addControl('terms', new FormControl('', [Validators.required, Validators.pattern('true')]));
        this.form.addControl('street_payment', new FormControl(''));
        this.form.addControl('city_payment', new FormControl(''));
        this.form.addControl('postal_code_payment', new FormControl(''));
        this.form.addControl('stripe_creditcard', new FormControl('', [Validators.required]));

        // disable stripe per default
        this.form.controls['stripe_creditcard'].disable({onlySelf: true});

        // first and lastname, if mandatory
        this.nameMandatory = this.optionService.getOption('has_name_mandatory') === true && this.optionService.getOption('has_contact_mandatory') !== true;
        if(this.nameMandatory === true) {
            this.form.addControl('firstname', new FormControl('', [Validators.required]));
            this.form.addControl('lastname', new FormControl('', [Validators.required]));
        }

        // preselect first payment_method if only one is available
        this.updateAvailablePaymentMethdods();
        if(this.donation.paymentMethod != null && this.availablePaymentMethods.indexOf(this.donation.paymentMethod) === -1) {
            // paymentmethod was once selected but is not longer available => deselt it
            this.donation.paymentMethod = null;
        }

        if (this.step !== 'start' && this.availablePaymentMethods.length > 0 && this.donation.paymentMethod == null && (this.size !== "small" || this.donation.isAmountZero())) {
            // preset first payment method
            this.donation.paymentMethod = this.availablePaymentMethods[0];
        }

        // preset paymentMethod
        if(this.donation.paymentMethod) {
            this.form.controls['payment_method'].setValue(this.donation.paymentMethod);
            this.form.controls['payment_method'].updateValueAndValidity();
            this.changePaymentMethod(this.size == "small");
        }

        let excludedFields = this.optionService.getOption("exclude_contact_fields");
        if (excludedFields) {
            this.excludedFields = excludedFields.split(",");
        }


        // update validators for address fields
        this.updateAddressFields();

        // check if we have contact data already => preset email field and preset salutation
        setTimeout(() => {
            this.presetEmailAndName();
            this.presetSalutation();
        }, 0);
    }

    /**
     * presets the email and name if it is possible
     */
    presetEmailAndName(): void {
        // email
        if(this.contact && this.contact.data.email && this.donation.userEmail == null) {
            this.form.controls['email'].setValue(this.contact.data.email);
            this.form.controls['email'].updateValueAndValidity();
            this.donation.userEmail = this.contact.data.email;
        }

        // name
        if((this.contact?.data.firstname || this.contact?.data.lastname) && this.donation.paymentDebit.name == null) {
            this.donation.paymentDebit.name = this.contact.data.firstname?.trim()?? '';
            if (this.contact.data.lastname) {
                this.donation.paymentDebit.name += " " + this.contact.data.lastname.trim();
            }
            this.form.controls['name'].setValue(this.donation.paymentDebit.name);
            this.form.controls['name'].updateValueAndValidity();
        }
    }

    /**
     * Function that is called when the component is destroyed
     */
    public ngOnDestroy(): any {
        // disable all validators on the form
        this.form.removeControl('payment_method');
        this.form.removeControl('email');
        this.form.removeControl('name');
        this.form.removeControl('firstname');
        this.form.removeControl('lastname');
        this.form.removeControl('iban');
        this.form.removeControl('bic');
        this.form.removeControl('terms');
        this.form.removeControl('street_payment');
        this.form.removeControl('postal_code_payment');
        this.form.removeControl('city_payment');
    }

    /**
     * Returns if the error class for this component should be shown
     * @returns {boolean}
     */
    paymentHasError(): boolean {
        if(this.form.controls['payment_method'].touched) {
            return this.form.controls['payment_method'].valid == false ||
                (this.form.controls['email'].touched == true && this.form.controls['email'].valid == false) ||
                (this.form.controls['name'].touched == true && this.form.controls['name'].enabled == true && this.form.controls['name'].valid == false) ||
                (this.form.controls['iban'].touched == true && this.form.controls['iban'].enabled == true && this.form.controls['iban'].valid == false) ||
                (this.form.controls['bic'].touched == true && this.form.controls['bic'].enabled == true && this.form.controls['bic'].valid == false) ||
                (this.form.controls['street_payment'].touched == true && this.form.controls['street_payment'].enabled == true && this.form.controls['street_payment'].valid == false) ||
                (this.form.controls['city_payment'].touched == true && this.form.controls['city_payment'].enabled == true && this.form.controls['city_payment'].valid == false) ||
                (this.form.controls['postal_code_payment'].touched == true && this.form.controls['postal_code_payment'].enabled == true && this.form.controls['postal_code_payment'].valid == false) ||
                (this.form.controls['terms'].touched == true && this.form.controls['terms'].enabled == true && this.form.controls['terms'].valid == false);
        }
        return false;
    }

    /**
     * Returns the textkey for the payment headline
     * @returns {string}
     */
    paymentHeadline({showError = true, onlySmall = false}): string {
        if (this.paymentHasError() && showError) {
            // input not valid
            return this.form.controls['payment_method'].valid === false ? "payment.error_no-selection" : "payment.error_wrong-input";
        }
        if ((this.amount + this.donation.extraAmount) === 0) {
            return "payment.headline.zero";
        }
        if (this.donation.paymentMethod && this.step !== "payment-select") {
            if (onlySmall && this.size === 'small' || !onlySmall) {
                // small size, payment selection on extra step
                let paymentMethod = this.donation.paymentMethod;
                // map some payment methods
                if (paymentMethod ===  'stripe_creditcard') {
                    paymentMethod = 'sixpay';
                }
                if (paymentMethod === 'paypal') {
                    if (this.isContactMandatory) {
                        return "payment.paypal.label";
                    }
                }
                
                return "payment.headline." + paymentMethod;
            }
        }
        // default case
        return "payment.headline.standard";
    }

    /**
     * Returns if the donation method selection should be shown or not
     *
     * @returns {boolean}
     */
    showDonationMethodSelection(): boolean {
        // shot on payment-selet step, on form version and on medium and large screens
        return (this.amount + this.donation.extraAmount) > 0 && (this.step == "payment-select" || this.version == "form" || this.size != "small");
    }

    /**
     * Open the infolayer if the link inside the SEPA label is clicked
     * @param event
     * @returns {boolean}
     */
    debitTermsClicked(event: any): boolean {
        if(event.target.localName == "a") {
            this.showInfotext = !this.showInfotext;
            event.preventDefault();
            return false
        }
        return true;
    }

    /**
     * Returns data from the organisation that can be used in the template
     * @param key
     * @returns {string}
     */
    getOrganisationData(key: string): any {
        return this.optionService.getOrganisationData(key);
    }

    /**
     * hint text for generic payment method
     */
    getGenericHint(): any {
        let genericOptionsHint = this.optionService.getOrganisationData("payment_generic");
        return genericOptionsHint.hint;
    }

    /**
     * get name for generic payment method
     */
    getGenericName(): any {
        let genericOptionsHint = this.optionService.getOrganisationData("payment_generic");
        return genericOptionsHint.name;
    }

    /**
     * Returns the name of the icon for a paymentMethod
     * @param paymentMethod
     * @returns {string}
     */
    getIcon(paymentMethod: string) {
        return getIconForPaymentMethod(paymentMethod, this.optionService);
    }

    /**
     * Checks if the addressfields are needed or not
     */
    isAddressNeeded(): boolean {
        switch(this.donation.paymentMethod) {
            case 'debit':
            case 'betterpayment':
            case 'konfipay':
                if(this.donation.paymentDebit.iban) {
                    const country = this.donation.paymentDebit.iban.substr(0, 2).toUpperCase();
                    return PaymentComponent.ibanCodesWhereAddressIsNeeded.indexOf(country) !== -1;
                }
        }
        return false;
    }

    /**
     * Should the BIC field be visible or not
     */
    showBic(): boolean {
        return this.needBic;
    }

    /**
     * Returns if a field is invalid
     * @param field
     * @returns {boolean}
     */
    isFieldInvalid(field: string): boolean {
        return this.form.controls[field] && this.form.controls[field].touched == true && this.form.controls[field].invalid == true;
    }

    /**
     * Returns the label textkey of a field, depending of it is valid or not
     * @param field
     * @returns {string}
     */
    getLabelKeyForField(field: string): string {
        return this.isFieldInvalid(field)? 'contact.' + field + '.label.error' : 'contact.' + field + '.label';
    }

    /**
     * Sets the salutation based on the gender determined from the firstname
     */
    presetSalutation(): void {
        // if field should be visible and contact is not mandatory
        if (this.excludedFields.indexOf('salutation') == -1 && this.optionService.getOption('has_contact_mandatory') !== true) {
            // and we have a firstname
            if (this.contact.data.firstname) {
                this.genderService.getGenderForName(this.contact.data.firstname).subscribe((response) => {
                    if (response === 'M') this.contact.data.salutation = 'male';
                    if (response === 'F') this.contact.data.salutation = 'female';
                    this.showSalutation = true;
                });
            }
        }
    }

    /**
     * Change the payment method to SEPA 
     */

    changePaymentToSepa(): void {
        for (const paymentMethod of this.availablePaymentMethods) {
            if (paymentMethod === 'debit' || paymentMethod === 'betterpayment' || paymentMethod === 'konfipay') {
                // set the payment method
                this.donation.paymentMethod = paymentMethod;
                this.changePaymentMethod(false);
                break; // break the loop after the first hit
            }
        }
    }

    /**
     * fix the email address typos
     */
    correctEmailAddress() {
        // get the email address
        const email = this.form.controls['email'].value;

        // fix the email address
        const correctedEmail = this.emailService.correctEmailAddress(email);

        // set the corrected email address
        this.form.controls['email'].setValue(correctedEmail);
        this.donation.userEmail = correctedEmail;
    }

}
